import { useState, useEffect, useCallback } from 'react'
import {
  FileText,
  Layout,
  Save,
  SendHorizonal,
  Eye,
  EyeOff,
  X,
  Info,
  Loader2,
} from 'lucide-react'
import api from '@/admin/lib/api'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { toast } from 'sonner'
import { EmailEditor } from '@/admin/components/email-editor'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

/* ── Types ─────────────────────────────────────────────── */

import { toastApiError } from '@/shared/lib/api-error'
interface EmailContentTemplate {
  id: number
  slug: string
  name: string
  subject: string
  body_html: string
  body_text: string
  updated_at: string
}

interface EmailWrapperTemplate {
  id: number
  slug: string
  name: string
  body_html: string
  updated_at: string
}

type SelectedTemplate =
  | { kind: 'content'; data: EmailContentTemplate }
  | { kind: 'wrapper'; data: EmailWrapperTemplate }

/* ── Variable reference per slug ───────────────────────── */

const CONTENT_VARIABLES: Record<string, string[]> = {
  password_reset: ['user_first_name', 'reset_link'],
  organisation_invitation: ['user_first_name', 'organisation_name', 'invitation_link'],
  magic_link: ['user_first_name', 'magic_link'],
  partnership_invitation: [
    'admin_first_name',
    'upstream_organisation_name',
    'partner_role_name',
    'invitation_link',
  ],
  product_registration: [
    'user_first_name',
    'product_name',
    'registration_date',
    'product_image_url',
    'product_link',
    'warranty_start_date',
    'warranty_expiry_date',
    'warranty_remaining_time_string',
  ],
  claim_submission: [
    'user_first_name',
    'product_name',
    'claim_reference',
    'submission_date',
    'claim_link',
  ],
  claim_status_update: [
    'user_first_name',
    'product_name',
    'claim_reference',
    'claim_link',
    'new_status',
    'status_message',
  ],
  claim_communication: [
    'user_first_name',
    'product_name',
    'claim_reference',
    'claim_link',
    'sender_name',
    'message',
  ],
  claim_assignment: [
    'assignee_first_name',
    'product_name',
    'claim_reference',
    'claim_link',
    'assigner_name',
    'organisation_name',
  ],
}

/* ── Component ─────────────────────────────────────────── */

export function EmailTemplatesPage() {

  const [contentTemplates, setContentTemplates] = useState<EmailContentTemplate[]>([])
  const [wrapperTemplates, setWrapperTemplates] = useState<EmailWrapperTemplate[]>([])
  const [selected, setSelected] = useState<SelectedTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testBranding, setTestBranding] = useState('default')
  const [sendingTest, setSendingTest] = useState(false)
  const [activeTab, setActiveTab] = useState<'html' | 'plain'>('html')
  const [showVariables, setShowVariables] = useState(false)

  /* ── Local editable state ──────────────────────────────── */
  const [editSubject, setEditSubject] = useState('')
  const [editHtml, setEditHtml] = useState('')
  const [editPlain, setEditPlain] = useState('')

  /* ── Fetch lists ───────────────────────────────────────── */

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const [contentRes, wrapperRes] = await Promise.all([
        api.get('/staff/email-templates/content/'),
        api.get('/staff/email-templates/wrappers/'),
      ])
      const content = Array.isArray(contentRes.data) ? contentRes.data : contentRes.data.results ?? []
      const wrappers = Array.isArray(wrapperRes.data) ? wrapperRes.data : wrapperRes.data.results ?? []
      setContentTemplates(content)
      setWrapperTemplates(wrappers)
    } catch (err) {
      toastApiError(err, 'Failed to load email templates');
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  /* ── Select a template ─────────────────────────────────── */

  const selectContent = useCallback(async (id: number) => {
    try {
      const res = await api.get(`/staff/email-templates/content/${id}/`)
      const data = res.data as EmailContentTemplate
      setSelected({ kind: 'content', data })
      setEditSubject(data.subject)
      setEditHtml(data.body_html)
      setEditPlain(data.body_text)
      setShowPreview(false)
      setActiveTab('html')
    } catch (err) {
      toastApiError(err, 'Failed to load template');
    }
  }, [])

  const selectWrapper = useCallback(async (id: number) => {
    try {
      const res = await api.get(`/staff/email-templates/wrappers/${id}/`)
      const data = res.data as EmailWrapperTemplate
      setSelected({ kind: 'wrapper', data })
      setEditHtml(data.body_html)
      setEditSubject('')
      setEditPlain('')
      setShowPreview(false)
    } catch (err) {
      toastApiError(err, 'Failed to load template');
    }
  }, [])

  /* ── Save ───────────────────────────────────────────────── */

  const handleSave = useCallback(async () => {
    if (!selected) return
    setSaving(true)
    try {
      if (selected.kind === 'content') {
        await api.put(`/staff/email-templates/content/${selected.data.id}/`, {
          subject: editSubject,
          body_html: editHtml,
          body_text: editPlain,
        })
      } else {
        await api.put(`/staff/email-templates/wrappers/${selected.data.id}/`, {
          body_html: editHtml,
        })
      }
      toast.success('Template saved')
      fetchTemplates()
    } catch (err) {
      toastApiError(err, 'Failed to save template');
    } finally {
      setSaving(false)
    }
  }, [selected, editSubject, editHtml, editPlain, fetchTemplates])

  /* ── Send test ──────────────────────────────────────────── */

  const handleSendTest = useCallback(async () => {
    if (!selected || selected.kind !== 'content') return
    setSendingTest(true)
    try {
      const body: Record<string, string> = { branding_option: testBranding }
      if (testEmail) body.recipient_email = testEmail
      await api.post(`/staff/email-templates/content/${selected.data.id}/test/`, body)
      toast.success('Test email sent')
      setShowTestDialog(false)
      setTestEmail('')
    } catch (err) {
      toastApiError(err, 'Failed to send test email');
    } finally {
      setSendingTest(false)
    }
  }, [selected, testEmail, testBranding])

  /* ── Sign out ───────────────────────────────────────────── */


  /* ── Variables for current template ─────────────────────── */

  const variables =
    selected?.kind === 'content' ? CONTENT_VARIABLES[selected.data.slug] ?? [] : []

  /* ── Render ─────────────────────────────────────────────── */

  return (
    <StaffShell>
      <div className="flex h-[calc(100vh-56px)]">
        {/* ── Sidebar ──────────────────────────────────────── */}
        <aside
          className="w-72 shrink-0 overflow-y-auto border-r-[0.5px] border-r-[var(--rule)] px-4 py-6"
        >
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
            </div>
          ) : (
            <>
              {/* Content Templates */}
              <div className="mb-6">
                <h3 className="clara-section-label mb-2 border-b-[0.5px] border-b-[var(--rule)] px-3 pb-2">
                  Content Templates
                </h3>
                <nav className="flex flex-col gap-0.5">
                  {contentTemplates.map((t) => {
                    const isActive =
                      selected?.kind === 'content' && selected.data.id === t.id
                    return (
                      <Button
                        variant="ghost"
                        key={t.id}
                        onClick={() => selectContent(t.id)}
                        className={cn(
                          `flex w-full items-center gap-2.5 rounded-[4px] px-3 py-2 text-left text-[12px] transition-all duration-150 cursor-pointer ${!isActive ? 'hover:bg-[var(--bg-subtle)]' : ''}`,
                          isActive
                            ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                            : 'text-[var(--ink-2)]',
                        )}
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{t.name}</span>
                      </Button>
                    );
                  })}
                </nav>
              </div>

              {/* Wrapper Templates */}
              <div>
                <h3 className="clara-section-label mb-2 border-b-[0.5px] border-b-[var(--rule)] px-3 pb-2">
                  Wrapper Templates
                </h3>
                <nav className="flex flex-col gap-0.5">
                  {wrapperTemplates.map((t) => {
                    const isActive =
                      selected?.kind === 'wrapper' && selected.data.id === t.id
                    return (
                      <Button
                        variant="ghost"
                        key={t.id}
                        onClick={() => selectWrapper(t.id)}
                        className={cn(
                          `flex w-full items-center gap-2.5 rounded-[4px] px-3 py-2 text-left text-[12px] transition-all duration-150 cursor-pointer ${!isActive ? 'hover:bg-[var(--bg-subtle)]' : ''}`,
                          isActive
                            ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                            : 'text-[var(--ink-2)]',
                        )}
                      >
                        <Layout className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{t.name}</span>
                      </Button>
                    );
                  })}
                </nav>
              </div>
            </>
          )}
        </aside>

        {/* ── Main panel ───────────────────────────────────── */}
        <section className="flex-1 overflow-y-auto">
          {!selected ? (
            <div className="flex justify-center pt-24">
              <p className="clara-small">Select a template to edit</p>
            </div>
          ) : (
            <div className="p-8 max-w-5xl">
              {/* Template name */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="clara-heading mb-1">{selected.data.name}</h1>
                  <p className="clara-small">
                    {selected.kind === 'content' ? 'Content' : 'Wrapper'} template
                    &middot; {selected.data.slug}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selected.kind === 'content' && variables.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setShowVariables(!showVariables)}
                      className={cn(
                        'flex items-center gap-1.5 rounded-[2px] border-[0.5px] border-[var(--rule-strong)] px-3 py-2 text-[11px] uppercase tracking-[0.1em] transition-colors',
                        showVariables
                          ? 'bg-[var(--green-bg)] text-[var(--green)]'
                          : 'bg-transparent text-[var(--ink-3)]',
                      )}
                    >
                      <Info className="h-3 w-3" />
                      Variables
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setShowPreview(!showPreview)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-[2px] border-[0.5px] border-[var(--rule-strong)] px-3 py-2 text-[11px] uppercase tracking-[0.1em] transition-colors',
                      showPreview
                        ? 'bg-[var(--green-bg)] text-[var(--green)]'
                        : 'bg-transparent text-[var(--ink-3)]',
                    )}
                  >
                    {showPreview ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                    Preview
                  </Button>
                  {selected.kind === 'content' && (
                    <div className="relative">
                      <Button
                        variant="outline"
                        onClick={() => setShowTestDialog(!showTestDialog)}
                        className="flex items-center gap-1.5 rounded-[2px] border-[0.5px] border-[var(--rule-strong)] px-3 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors"
                      >
                        <SendHorizonal className="h-3 w-3" />
                        Send Test
                      </Button>
                      {showTestDialog && (
                        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] p-4 shadow-[var(--shadow-float)]">
                          <div className="flex items-center justify-between mb-3">
                            <span className="clara-section-label">Send Test Email</span>
                            <Button
                              variant="ghost"
                              onClick={() => setShowTestDialog(false)}
                              className="text-[var(--ink-3)]"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="mb-3">
                            <Label className="clara-label block mb-1">
                              Recipient Email (optional)
                            </Label>
                            <Input
                              type="email"
                              value={testEmail}
                              onChange={(e) => setTestEmail(e.target.value)}
                              placeholder="Leave blank for your email"
                              className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)] outline-none"
                            />
                          </div>
                          <div className="mb-4">
                            <Label className="clara-label block mb-1">
                              Branding Option
                            </Label>
                            <Input
                              type="text"
                              value={testBranding}
                              onChange={(e) => setTestBranding(e.target.value)}
                              className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)] outline-none"
                            />
                          </div>
                          <Button
                            onClick={handleSendTest}
                            disabled={sendingTest}
                            className="w-full flex items-center justify-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-[0.1em] rounded-[2px] transition-colors cursor-pointer disabled:opacity-50 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
                          >
                            {sendingTest ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <SendHorizonal className="h-3 w-3" />
                            )}
                            {sendingTest ? 'Sending...' : 'Send'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-[0.1em] rounded-[2px] transition-colors cursor-pointer disabled:opacity-50 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
                  >
                    {saving ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Save className="h-3 w-3" />
                    )}
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>

              {/* Variable reference panel */}
              {showVariables && variables.length > 0 && (
                <div className="mb-6 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
                  <p className="clara-section-label mb-2">Available Variables</p>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((v) => (
                      <code
                        key={v}
                        className="rounded-[2px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-2 py-1 font-mono text-[11px] text-[var(--green)]"
                      >
                        {'{{ ' + v + ' }}'}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {/* Subject (content templates only) */}
              {selected.kind === 'content' && (
                <div className="mb-5">
                  <Label className="clara-label block mb-1.5">Subject</Label>
                  <Input
                    type="text"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    className="w-full px-3 py-2.5 text-[13px] rounded-[2px] outline-none transition-all duration-150 border-[0.5px] border-solid border-[var(--rule-strong)] focus:border-[var(--green)]"
                    className="w-full rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2.5 text-[13px] text-[var(--ink)] outline-none transition-all duration-150 focus:border-[var(--green)]"
                  />
                </div>
              )}

              {/* Tab bar for content templates */}
              {selected.kind === 'content' && (
                <div className="mb-0 flex gap-0 border-b-[0.5px] border-b-[var(--rule)]">
                  <Button
                    variant="ghost"
                    onClick={() => setActiveTab('html')}
                    className={cn(
                      'px-4 py-2 text-[11px] uppercase tracking-[0.1em] transition-colors border-b-2',
                      activeTab === 'html'
                        ? 'border-b-[var(--green)] text-[var(--green)]'
                        : 'border-b-transparent text-[var(--ink-3)]',
                    )}
                  >
                    HTML Body
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setActiveTab('plain')}
                    className={cn(
                      'px-4 py-2 text-[11px] uppercase tracking-[0.1em] transition-colors border-b-2',
                      activeTab === 'plain'
                        ? 'border-b-[var(--green)] text-[var(--green)]'
                        : 'border-b-transparent text-[var(--ink-3)]',
                    )}
                  >
                    Plain Text
                  </Button>
                </div>
              )}

              {/* Editor */}
              {showPreview ? (
                <div className="mt-4 overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)]">
                  <iframe
                    sandbox=""
                    srcDoc={editHtml}
                    title="Email preview"
                    className="h-[600px] w-full border-none bg-[var(--bg)]"
                  />
                </div>
              ) : (
                <div className="mt-4">
                  {selected.kind === 'wrapper' ? (
                    /* Wrapper templates use raw HTML - WYSIWYG would mangle {{ content }} */
                    (<textarea
                      value={editHtml}
                      onChange={(e) => setEditHtml(e.target.value)}
                      spellCheck={false}
                      className="min-h-[500px] w-full resize-y rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] p-4 font-mono text-[12px] leading-relaxed text-[var(--ink)] outline-none transition-all duration-150 [tab-size:2] focus:border-[var(--green)]"
                    />)
                  ) : activeTab === 'html' ? (
                    /* Content templates get WYSIWYG with HTML toggle */
                    (<EmailEditor
                      value={editHtml}
                      onChange={setEditHtml}
                      label="HTML Body"
                    />)
                  ) : (
                    <textarea
                      value={editPlain}
                      onChange={(e) => setEditPlain(e.target.value)}
                      spellCheck={false}
                      className="min-h-[500px] w-full resize-y rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] p-4 font-mono text-[12px] leading-relaxed text-[var(--ink)] outline-none transition-all duration-150 [tab-size:2] focus:border-[var(--green)]"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </StaffShell>
  );
}
